import { hashKey, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import useLatest from "@-/com.npmjs.use-latest";
import { createPlainConsola } from "@-/dev.improve.haniverse.lib.logger";

const consola = createPlainConsola({ defaults: { tag: "usePrefetchQueries" } });

type QueryClientPrefetchQueryOptions = Parameters<QueryClient["prefetchQuery"]>[0];

/** Same shape as `useQueries` — a `queries` array of TanStack Query option objects. */
export type UsePrefetchQueriesOptions = {
  onSuccess?: () => void;
  queries: readonly QueryClientPrefetchQueryOptions[];
  queryClient?: QueryClient;
};

// Serialize the queries' queryKeys into ONE stable string. Our page navigators return a FRESH
// `queryOptions({…})` object every render on purpose (the `require-query-key-getter` lint rule),
// so `queries` is a new array reference every render even when nothing changed. Keying the effect
// on the array REFERENCE below re-fired the prefetch on EVERY render — under the post-sign-in
// actor-sync re-render burst this became a prefetch STORM that kept the app from ever going idle
// (maestro's "QueryController: Could not detect idle state" + a wedged Learn screen). Keying on the
// hashed queryKeys instead re-fires the prefetch ONLY when a query's key actually changes.
function hashQueryKeys(queries: readonly QueryClientPrefetchQueryOptions[]): string {
  return queries.map((queryOptions) => hashKey(queryOptions.queryKey ?? [])).join("|");
}

export function usePrefetchQueries({
  onSuccess,
  queries,
  queryClient: queryClientOption,
}: UsePrefetchQueriesOptions): void {
  const queryClientFromContext = useQueryClient();
  const queryClient = queryClientOption ?? queryClientFromContext;

  // Read `queries`/`onSuccess` through refs so the effect can depend ONLY on the stable queryKey
  // hash (below) without going stale — otherwise the churning `queries`/`onSuccess` identities
  // would have to be in the dep array, reintroducing the every-render re-fire.
  const queriesRef = useLatest(queries);
  const onSuccessRef = useLatest(onSuccess);
  const queriesKeyHash = hashQueryKeys(queries);

  // Deliberately keyed on the STABLE `queriesKeyHash` (the hashed queryKeys), NOT the churning
  // `queries`/`onSuccess` references — those are read through `useLatest` refs inside so the body
  // stays fresh without the every-render re-fire. `queriesRef`/`onSuccessRef` are stable ref objects
  // (never change identity), so omitting them from the dep array is correct; see `hashQueryKeys`.
  useEffect(() => {
    void Promise.all(
      queriesRef.current.map((queryOptions: QueryClientPrefetchQueryOptions) =>
        queryClient.prefetchQuery(queryOptions),
      ),
    )
      .then(() => {
        onSuccessRef.current?.();
      })
      .catch((error: unknown) => {
        consola.error("Failed to prefetch some queries", error);
      });
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- see comment above the effect: keyed on the stable queryKey hash; queriesRef/onSuccessRef are stable useLatest refs
  }, [queryClient, queriesKeyHash]);
}
